import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Card, Space } from 'antd';
import { ReloadOutlined, BugOutlined, HomeOutlined } from '@ant-design/icons';
import { AriaHelper, LoadingStateHelper } from '../../utils/accessibility';
import { colors, spacing } from './tokens';

interface Props {
  children: ReactNode;
  /** Fallback component to render on error */
  fallback?: ReactNode;
  /** Whether to show detailed error information */
  showDetails?: boolean;
  /** Custom error title */
  title?: string;
  /** Custom error message */
  message?: string;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show retry button */
  showRetry?: boolean;
  /** Whether to show home button */
  showHome?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  private errorId: string;

  constructor(props: Props) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };

    this.errorId = AriaHelper.generateId('error-boundary');
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);

    // Announce error to screen readers
    AriaHelper.announce('An error has occurred. Please check the error message and try again.', 'assertive');
    
    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      // Example: reportError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    
    AriaHelper.announce('Retrying...', 'polite');
  };

  handleHome = () => {
    window.location.href = '/dashboard';
    AriaHelper.announce('Navigating to home page', 'polite');
  };

  render() {
    if (this.state.hasError) {
      const { 
        fallback,
        showDetails = process.env.NODE_ENV === 'development',
        title = 'Something went wrong',
        message = 'An unexpected error occurred. Please try refreshing the page.',
        showRetry = true,
        showHome = true,
      } = this.props;

      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      return (
        <div
          style={{
            padding: spacing['2xl'],
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.background.primary,
          }}
        >
          <Card
            style={{
              maxWidth: '600px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Error Icon */}
              <div
                style={{
                  fontSize: '48px',
                  color: colors.status.negative,
                  marginBottom: spacing.lg,
                }}
              >
                <BugOutlined />
              </div>

              {/* Main Error Alert */}
              <Alert
                message={title}
                description={message}
                type="error"
                showIcon
                role="alert"
                aria-describedby={showDetails ? `${this.errorId}-details` : undefined}
              />

              {/* Action Buttons */}
              <Space>
                {showRetry && (
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={this.handleRetry}
                    aria-label="Retry the operation that failed"
                  >
                    Try Again
                  </Button>
                )}
                
                {showHome && (
                  <Button
                    icon={<HomeOutlined />}
                    onClick={this.handleHome}
                    aria-label="Go to home page"
                  >
                    Go Home
                  </Button>
                )}
              </Space>

              {/* Detailed Error Information (Development) */}
              {showDetails && this.state.error && (
                <Card
                  id={`${this.errorId}-details`}
                  title="Error Details (Development Only)"
                  size="small"
                  style={{
                    textAlign: 'left',
                    backgroundColor: colors.background.secondary,
                    marginTop: spacing.lg,
                  }}
                >
                  <div style={{ marginBottom: spacing.sm }}>
                    <strong>Error:</strong>
                    <pre style={{
                      fontSize: '12px',
                      backgroundColor: colors.background.tertiary,
                      padding: spacing.sm,
                      borderRadius: '4px',
                      overflow: 'auto',
                      marginTop: spacing.xs,
                    }}>
                      {this.state.error.toString()}
                    </pre>
                  </div>
                  
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre style={{
                        fontSize: '12px',
                        backgroundColor: colors.background.tertiary,
                        padding: spacing.sm,
                        borderRadius: '4px',
                        overflow: 'auto',
                        marginTop: spacing.xs,
                        maxHeight: '200px',
                      }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </Card>
              )}

              {/* Help Text */}
              <div style={{
                fontSize: '14px',
                color: colors.text.secondary,
                marginTop: spacing.lg,
              }}>
                If this problem persists, please contact support with the error details above.
              </div>
            </Space>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary for functional components
 */
interface UseErrorBoundaryReturn {
  showBoundary: (error: Error) => void;
}

export const useErrorBoundary = (): UseErrorBoundaryReturn => {
  return {
    showBoundary: (error: Error) => {
      // This would typically integrate with a state management solution
      // For now, we'll throw the error to be caught by the nearest error boundary
      throw error;
    },
  };
};

/**
 * Simple error display component for known errors
 */
interface ErrorDisplayProps {
  error: Error | string;
  title?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  title = 'Error',
  showRetry = false,
  onRetry,
  size = 'default',
  style,
}) => {
  const errorMessage = typeof error === 'string' ? error : error.message;
  
  const handleRetry = () => {
    if (onRetry) {
      AriaHelper.announce('Retrying...', 'polite');
      onRetry();
    }
  };

  return (
    <div style={{ padding: size === 'small' ? spacing.sm : spacing.lg, ...style }}>
      <Alert
        message={title}
        description={errorMessage}
        type="error"
        showIcon
        role="alert"
        action={
          showRetry && onRetry ? (
            <Button
              size="small"
              type="text"
              onClick={handleRetry}
              icon={<ReloadOutlined />}
            >
              Retry
            </Button>
          ) : undefined
        }
      />
    </div>
  );
};

/**
 * Network error specific component
 */
interface NetworkErrorProps {
  onRetry?: () => void;
  message?: string;
}

export const NetworkError: React.FC<NetworkErrorProps> = ({
  onRetry,
  message = 'Unable to connect to the server. Please check your internet connection.',
}) => {
  return (
    <ErrorDisplay
      error={message}
      title="Connection Error"
      showRetry={!!onRetry}
      onRetry={onRetry}
    />
  );
};

/**
 * Permission error component
 */
interface PermissionErrorProps {
  resource?: string;
  action?: string;
}

export const PermissionError: React.FC<PermissionErrorProps> = ({
  resource = 'this resource',
  action = 'access',
}) => {
  const message = `You don't have permission to ${action} ${resource}. Please contact your administrator if you believe this is an error.`;
  
  return (
    <ErrorDisplay
      error={message}
      title="Access Denied"
      showRetry={false}
    />
  );
};

export default ErrorBoundary;